import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ROUTE_MAP } from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import {
  setShowingMenuBar,
  setShowMapOverlay,
  setSideBar,
} from '../../../services/store/modules/app-controls';
import PageHolderContent from '../../containers/PageHolderContent/PageHolderContent';
import { back, close } from '../../shared/vector-images';
import HeaderBar from '../components/HeaderBar/HeaderBar';
import HeaderBarButton from '../components/HeaderBarButton/HeaderBarButton';

const HeaderImage = styled.img`
  max-width: 150px;
  margin-bottom: ${({ theme }) => `${theme.space[3]}px`};
`;

interface StyledBackgroundOffsetWrapperProps {
  image: string;
  offset: number;
}
const StyledBackgroundOffsetWrapper = styled.div<StyledBackgroundOffsetWrapperProps>`
  width: 100%;
  overflow: visible;
  height: ${({ offset }) => `${offset}px`};
`;

const StyledImg = styled.img`
  width: 100%;
`;

const StyledWrapper = styled.div`
  max-height: 250px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledBackgroundOffset: FC<{
  image: string;
}> = ({ image }) => {
  const imageContainer = useRef<HTMLDivElement>(null);
  const [offsetHeight, setOffsetHeight] = useState(0);

  // This component has a few layers to it.
  // 1. A wrapper component creates the padding at the top of the layout.
  // 2. The inner wrapper is what is used to make sure the image doesn't stretch out of proportion and
  // make sure that the image height isn't above 250px. That value is arbitrary and can be changed, however
  // it's important to have in place as this image may be data-drive, and we want to restrict hug images from
  // consuming all of the space
  // 3. The inner img displays the image

  useEffect((): (() => void | undefined) => {
    if (!imageContainer.current) return () => undefined;
    const resizeObserver = new ResizeObserver(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const height =
        imageContainer.current?.getBoundingClientRect()?.height || 0;

      setOffsetHeight(height * 0.75);
    });
    resizeObserver.observe(imageContainer.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <StyledBackgroundOffsetWrapper image={image} offset={offsetHeight}>
      <StyledWrapper ref={imageContainer}>
        <StyledImg alt="" src={image} />
      </StyledWrapper>
    </StyledBackgroundOffsetWrapper>
  );
};

export interface BasicDrawerLayoutProps {
  /**
   * Renders an image behind the main content of the layout.
   */
  backgroundImage?: string;

  /**
   * The heading text to display at the top of the page
   */
  heading?: string;

  /**
   * Render function to replace the default header bar
   */
  renderHeaderBar?: ReactNode;

  /**
   * Can opt-in to hiding the headerbar
   */
  showHeaderBar?: boolean;

  /**
   * Option to hide the menu bar of the app
   */
  showMenuBar?: boolean;

  /**
   * Puts a backdrop under the layout and ontop of the map
   */
  showMapOverlay?: boolean;

  /**
   * @deprecated passes through a loading property through to the underlying `PageHolderContent` component
   */
  loading?: boolean;

  /**
   * If provided, and back arrow is showing and will execute the supplied function
   */
  onBack?: () => void;

  /**
   * If provided, a close button is showing and will execute the supplied function
   */
  onClose?: (() => void) | null;
}

const DefaultHeader = ({
  heading,
  onBack,
  onClose,
}: Pick<BasicDrawerLayoutProps, 'heading' | 'onBack' | 'onClose'>) => (
  <HeaderBar
    solidBackground
    centerNode={heading}
    leftNode={
      onBack && (
        <HeaderBarButton ariaLabel="return" icon={back()} onClick={onBack} />
      )
    }
    rightNode={
      onClose && (
        <HeaderBarButton ariaLabel="close" icon={close()} onClick={onClose} />
      )
    }
  />
);

const BasicDrawerLayout: FC<BasicDrawerLayoutProps> & {
  HeaderImage: FC<{ src?: string | null; alt: string }>;
} = ({
  backgroundImage,
  showHeaderBar,
  showMenuBar = true,
  showMapOverlay,
  heading,
  loading,
  onBack,
  onClose,
  children,
  renderHeaderBar,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setSideBar());
    dispatch(setShowingMenuBar(showMenuBar));
    dispatch(setShowMapOverlay(showMapOverlay));
  }, [dispatch, showMapOverlay, showMenuBar]);

  return (
    <PageHolderContent
      isLoading={loading}
      headerBarContent={
        showHeaderBar &&
        (renderHeaderBar || (
          <DefaultHeader heading={heading} onBack={onBack} onClose={onClose} />
        ))
      }
    >
      {backgroundImage && <StyledBackgroundOffset image={backgroundImage} />}
      {children}
    </PageHolderContent>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
BasicDrawerLayout.HeaderImage = HeaderImage;

BasicDrawerLayout.defaultProps = {
  backgroundImage: undefined,
  heading: undefined,
  showHeaderBar: true,
  showMenuBar: true,
  showMapOverlay: false,
  loading: false,
  onBack: undefined,
  renderHeaderBar: undefined,
  onClose: () => transitionToNextRoute(ROUTE_MAP),
};

export default BasicDrawerLayout;
